import { useSearchParams } from 'react-router';
import { useComptesTresorerie, useDossiersTresorerie } from '@/api/hooks';
import { LIBELLE_STATUT_DOSSIER, STATUTS_DOSSIER, type StatutDossier } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { OngletsTresorerie, TableDossiers, decalerSemaine, libelleSemaine, semaineCourante } from '@/components/tresorerie';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { montant } from '@/lib/utils';

/** Liste des FED en trésorerie (RT-2) : filtres par statut, semaine du plan, compte et recherche. */
export function DossiersListe() {
  const [params, setParams] = useSearchParams();
  const statut = (params.get('statut') ?? '') as StatutDossier | '';
  const semaine = params.get('semaine') ?? '';
  const compteId = params.get('compteId') ?? '';
  const q = params.get('q') ?? '';
  const maj = (patch: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };
  const { data, isLoading, error } = useDossiersTresorerie({ statut: statut || undefined, semaine: semaine || undefined, compteId: compteId || undefined, q: q || undefined });
  const comptes = useComptesTresorerie();
  const courante = semaineCourante();
  const semaines = [decalerSemaine(courante, -1), courante, decalerSemaine(courante, 1), decalerSemaine(courante, 2), decalerSemaine(courante, 3)];
  const total = (data ?? []).reduce((s, d) => s + d.fed.montantUsd, 0);

  return (
    <div>
      <PageHeader titre="FED en trésorerie" sousTitre={data ? `${data.length} dossier(s) · ${montant(total, true)}` : undefined} />
      <OngletsTresorerie />
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <SelectNatif value={statut} onChange={(e) => maj({ statut: e.target.value })} aria-label="Statut">
          <option value="">Tous les statuts</option>
          {STATUTS_DOSSIER.map((s) => (
            <option key={s} value={s}>
              {LIBELLE_STATUT_DOSSIER[s]}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={semaine} onChange={(e) => maj({ semaine: e.target.value })} aria-label="Semaine du plan">
          <option value="">Toutes les semaines</option>
          {[...new Set([...semaines, ...(semaine ? [semaine] : [])])].sort().map((s) => (
            <option key={s} value={s}>
              {libelleSemaine(s)}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={compteId} onChange={(e) => maj({ compteId: e.target.value })} aria-label="Compte">
          <option value="">Tous les comptes</option>
          {(comptes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.banque} · {c.code}
            </option>
          ))}
        </SelectNatif>
        <Input value={q} onChange={(e) => maj({ q: e.target.value })} placeholder="Numéro, objet, bénéficiaire" aria-label="Recherche" />
      </div>
      {isLoading ? <Chargement /> : error || !data ? <ErreurBloc erreur={error} /> : <TableDossiers dossiers={data} vide="Aucun FED ne correspond aux filtres." colonnes={['statut', 'semaine', 'mode', 'tresorier', 'delai']} />}
    </div>
  );
}
